import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { getStorage, ref, getDownloadURL } from '@firebase/storage'

export type RepairEstimatePdfCompareItem = {
  label: string
  storagePath: string
  uploadedAt?: string
  note?: string
}

export function useRepairWorkNoteDownloadUrl(storagePath: string | undefined) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }

    let cancelled = false
    setStatus('loading')
    void getDownloadURL(ref(getStorage(), storagePath))
      .then((downloadUrl) => {
        if (cancelled) return
        setUrl(downloadUrl)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setUrl(null)
        setStatus('error')
      })

    return () => {
      cancelled = true
    }
  }, [storagePath])

  return { url, status }
}

const PdfPanel: React.FC<{ item: RepairEstimatePdfCompareItem }> = ({ item }) => {
  const { url, status } = useRepairWorkNoteDownloadUrl(item.storagePath)

  return (
    <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" fontWeight={700}>
            {item.label}
          </Typography>
          {item.uploadedAt ? (
            <Typography variant="caption" color="text.secondary">
              Uploaded {item.uploadedAt}
            </Typography>
          ) : null}
          {item.note ? (
            <Typography variant="caption" color="text.secondary">
              {item.note}
            </Typography>
          ) : null}
        </Stack>
        {url ? (
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Open PDF
          </Button>
        ) : null}
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 360,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {status === 'loading' || status === 'idle' ? (
          <CircularProgress size={28} />
        ) : status === 'error' || !url ? (
          <Typography variant="body2" color="error">
            Could not load PDF preview.
          </Typography>
        ) : (
          <Box
            component="iframe"
            src={url}
            title={item.label}
            sx={{ width: '100%', height: { xs: 360, md: 480 }, border: 0, display: 'block' }}
          />
        )}
      </Box>
    </Stack>
  )
}

interface RepairEstimatePdfCompareDialogProps {
  open: boolean
  onClose: () => void
  older: RepairEstimatePdfCompareItem | null
  newer: RepairEstimatePdfCompareItem | null
}

export const RepairEstimatePdfCompareDialog: React.FC<RepairEstimatePdfCompareDialogProps> = ({
  open,
  onClose,
  older,
  newer,
}) => {
  const theme = useTheme()
  const isWide = useMediaQuery(theme.breakpoints.up('md'))
  const [activeTab, setActiveTab] = useState<'older' | 'newer'>('newer')

  useEffect(() => {
    if (open) {
      setActiveTab('newer')
    }
  }, [open, newer?.storagePath])

  if (!older || !newer) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <CompareArrowsIcon color="primary" />
        Compare estimate PDFs
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PictureAsPdfIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Review both estimate PDFs from the repair timeline.
            </Typography>
          </Stack>

          {isWide ? (
            <Stack direction="row" spacing={2} sx={{ minHeight: 500 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PdfPanel item={older} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PdfPanel item={newer} />
              </Box>
            </Stack>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={(_event, value: 'older' | 'newer') => setActiveTab(value)}
                variant="fullWidth"
              >
                <Tab value="older" label={older.label} />
                <Tab value="newer" label={newer.label} />
              </Tabs>
              <PdfPanel item={activeTab === 'older' ? older : newer} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
